// One quote/place controller shared by every betting presentation (the
// BetSlip drawer and the V2 racecard/rail). Odds display can differ per
// surface; the money path — request shape, authoritative quote, placement
// idempotency, cache invalidation — must not.

import type { QueryClient } from "@tanstack/react-query";

import { api } from "@/api";

import type { BetSlipLeg } from "./BetSlipProvider";

export type CouponLegRequest = {
  raceId: string;
  betType: string;
  sel: number[];
};

export type CouponRequest = {
  type: "single" | "combined" | "system";
  systemK?: number;
  stakeCents: number;
  legs: CouponLegRequest[];
};

export type CouponReceipt = {
  couponId: string | null;
  status: string;
  walletState: string;
  result: string | null;
  returnedCents: number | null;
  raw: Record<string, unknown>;
};

export function buildCouponRequest(input: {
  couponType: "single" | "combined" | "system";
  systemK: number | null;
  stakeCents: number;
  legs: BetSlipLeg[];
}): CouponRequest {
  return {
    type: input.couponType,
    systemK: input.systemK ?? undefined,
    stakeCents: input.stakeCents,
    legs: input.legs.map((leg) => ({
      raceId: leg.raceId,
      betType: leg.betType,
      sel: leg.sel,
    })),
  };
}

export function couponQuoteOptions(request: CouponRequest) {
  return {
    queryKey: ["betslip", "quote", request] as const,
    queryFn: () => api.quoteCoupon(request),
    staleTime: 0,
    retry: false,
  };
}

export function normalizeCouponReceipt(
  raw: Record<string, unknown>,
): CouponReceipt {
  return {
    couponId: typeof raw.couponId === "string" ? raw.couponId : null,
    status: typeof raw.status === "string" ? raw.status : "open",
    walletState: typeof raw.walletState === "string" ? raw.walletState : "",
    result: typeof raw.result === "string" ? raw.result : null,
    returnedCents:
      typeof raw.returnedCents === "number" ? raw.returnedCents : null,
    raw,
  };
}

export function couponReconciling(receipt: CouponReceipt): boolean {
  return receipt.status === "pending" || receipt.walletState === "reconciling";
}

/** The single money-moving call. `clientRef` is the idempotency anchor:
 * replaying it returns the original coupon, never a duplicate stake. */
export async function placeCouponRequest(
  queryClient: QueryClient,
  request: CouponRequest,
  clientRef: string,
): Promise<CouponReceipt> {
  const coupon = await api.placeCoupon({ ...request, clientRef });
  void queryClient.invalidateQueries({ queryKey: ["bets"] });
  void queryClient.invalidateQueries({ queryKey: ["balance"] });
  return normalizeCouponReceipt(coupon);
}
